import React, { useRef, useState, useLayoutEffect } from "react";
import { string, number, bool, shape } from "prop-types";
import { generateNearlyUniqueID } from "common/uuid";
import Loading from "common/components/loading";
import FacebookAdPreview from "common/components/facebook-ad-preview";
import InstagramAdPreview from "common/components/instagram-ad-preview";
import ReplayButton from "./replay-button";
import FacebookNewsfeedDataPropTypes from "../../prop-types/facebook-newsfeed-data-prop-types";

const propTypes = {
  url: string.isRequired,
  title: string.isRequired,
  mimeType: string.isRequired,
  width: number.isRequired,
  height: number.isRequired,
  placement: string,
  showReplayButton: bool,
  exactSize: bool,
  loading: bool,
  facebookNewsfeedData: FacebookNewsfeedDataPropTypes,
  facebookPage: shape({
    name: string,
    picture: string
  })
};

const DEFAULT_FACEBOOK_NEWSFEED_DATA = {
  body: "",
  title: "",
  message: "",
  domain: ""
};

const DEFAULT_FACEBOOK_PAGE = {
  name: "Your Facebook Page",
  picture: ""
};

const AdDesignPreview = ({
  url,
  title,
  mimeType,
  width,
  height,
  placement = null,
  showReplayButton = false,
  exactSize = false,
  loading = false,
  facebookNewsfeedData = DEFAULT_FACEBOOK_NEWSFEED_DATA,
  facebookPage = DEFAULT_FACEBOOK_PAGE
}) => {
  const ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(loading || false);
  let uuid = null;

  useLayoutEffect(() => {
    setContainerWidth(ref.current.clientWidth);
    uuid = generateNearlyUniqueID();
  }, []);

  const calculatedWidth = containerWidth > width || exactSize ? width : containerWidth;
  const proportionalHeight =
    containerWidth > width || exactSize ? height : containerWidth / (width / height);

  const handleReplayButtonClick = () => {
    const loadTime = 800;

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
    }, loadTime);
  };

  if (placement && placement.includes("facebook")) {
    return (
      <div ref={ref}>
        <FacebookAdPreview
          key="sample-creative-facebook-preview"
          image={{ image: url, type: "Facebook" }}
          width="248px"
          facebookPage={facebookPage}
          facebookNewsfeedData={facebookNewsfeedData}
        />
      </div>
    );
  }
  if (placement && placement.includes("instagram")) {
    return (
      <div ref={ref}>
        <InstagramAdPreview
          key="sample-creative-instagram-preview"
          image={{ image: url, type: "Instagram" }}
          width="270px"
          facebookPage={facebookPage}
          facebookNewsfeedData={facebookNewsfeedData}
        />
      </div>
    );
  }
  return (
    <div ref={ref}>
      {mimeType === "text/html" && (
        <>
          {isLoading ? (
            <div
              className="bg-light solid-gray-border d-flex align-items-center justify-content-center"
              style={{ width: calculatedWidth, height: proportionalHeight, margin: "0 auto" }}
            >
              <Loading loading size={2} />
            </div>
          ) : (
            <div style={{ width: calculatedWidth, margin: "0 auto" }}>
              <div
                className="position-relative"
                style={{
                  width: calculatedWidth,
                  minHeight: proportionalHeight
                }}
              >
                <div className="ad-preview-placeholder" />
                <iframe
                  title={title}
                  id={uuid}
                  data-testid="iframe"
                  className="border-0 ad-preview-iframe preview-on-top"
                  scrolling="no"
                  sandbox="allow-scripts allow-same-origin"
                  height={proportionalHeight}
                  width={calculatedWidth}
                  src={url}
                />
              </div>
              {showReplayButton && (
                <div className="d-flex">
                  <ReplayButton
                    buttonText="replay"
                    iframeSelector={`#${uuid}`}
                    onClick={handleReplayButtonClick}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
      {mimeType === "image/jpeg" && (
        <img
          src={url}
          alt={title}
          title={title}
          height={proportionalHeight}
          width={calculatedWidth}
          style={{ margin: "0 auto" }}
        />
      )}
      {mimeType === "video/mp4" && (
        <video
          disablePictureInPicture
          autoPlay
          muted
          loop
          controls
          controlsList="mediaSrc"
          src={url}
          data-testid="video"
          className="newsfeed-item__media-element"
          width={calculatedWidth}
          height={proportionalHeight}
          style={{ margin: "0 auto" }}
        />
      )}
    </div>
  );
};

AdDesignPreview.propTypes = propTypes;

export default AdDesignPreview;
